import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Heart, Loader2, Check, X, Sparkles, Search } from 'lucide-react';
import api from '../lib/api';

/**
 * "Apoyá a un Bravx" — referral input shown in checkout.
 * Live typeahead: as the buyer types, we surface matching @usernames + full names.
 * Final selection is validated via `/api/bravx/lookup/{username}`.
 */
export default function BravxReferralInput({
  value = '',
  onChange,
  totalAmount = 0,
  currentUserId = null,
  disabled = false,
}) {
  const [state, setState] = useState('idle'); // idle | searching | found | self | not_found | invalid
  const [match, setMatch] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const debounceRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const estimatedPoints = totalAmount > 0 ? Math.floor(totalAmount / 100) : 0;

  // Recalculate dropdown position whenever it should be visible
  const updateDropdownPosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  };

  // Keep position aligned on scroll/resize while dropdown is open
  useEffect(() => {
    if (!showDropdown) return;
    updateDropdownPosition();
    const handler = () => updateDropdownPosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [showDropdown]);

  // ---- Live validate the current value (exact lookup) -------------------
  useEffect(() => {
    const trimmed = (value || '').trim().replace(/^@/, '');
    if (!trimmed) {
      setState('idle');
      setMatch(null);
      return;
    }
    if (trimmed.length < 3) {
      setState('invalid');
      setMatch(null);
      return;
    }
    setState('searching');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/bravx/lookup/${encodeURIComponent(trimmed)}`);
        const data = res.data || {};
        if (data.available) {
          setState('not_found');
          setMatch(null);
        } else if (data.is_self) {
          setState('self');
          setMatch(data);
        } else {
          setState('found');
          setMatch(data);
        }
      } catch (e) {
        setState('not_found');
        setMatch(null);
      }
    }, 350);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, currentUserId]);

  // ---- Live typeahead suggestions ----------------------------------------
  useEffect(() => {
    const trimmed = (value || '').trim().replace(/^@/, '');
    if (!isFocused) return; // only suggest while focused
    if (trimmed.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/bravx/search', { params: { q: trimmed, limit: 8 } });
        const list = (res.data?.results || []).filter(r => !r.is_self);
        setSuggestions(list);
        setShowDropdown(list.length > 0);
        setActiveIdx(-1);
      } catch (e) {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 200);

    return () => searchDebounceRef.current && clearTimeout(searchDebounceRef.current);
  }, [value, isFocused]);

  // ---- Close dropdown on outside click -----------------------------------
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inMain = containerRef.current && containerRef.current.contains(e.target);
      const inDropdown = e.target.closest?.('[data-testid="bravx-referral-dropdown"]');
      if (!inMain && !inDropdown) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const pickSuggestion = (sug) => {
    onChange(sug.username);
    setSuggestions([]);
    setShowDropdown(false);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const helper = (() => {
    switch (state) {
      case 'searching':
        return { color: 'text-white/40', icon: <Loader2 size={12} className="animate-spin" />, text: 'Buscando…' };
      case 'found':
        return {
          color: 'text-emerald-400',
          icon: <Check size={12} />,
          text: `Vas a apoyar a ${match?.display_name || '@' + match?.username}. Suman ${estimatedPoints} pts cada uno.`,
        };
      case 'self':
        return {
          color: 'text-amber-400',
          icon: <X size={12} />,
          text: 'Ese sos vos. No podés apoyarte a vos mismo, pero podés apoyar a otro Bravx.',
        };
      case 'not_found':
        return {
          color: 'text-red-400',
          icon: <X size={12} />,
          text: 'No encontramos ese usuario en Bravo.',
        };
      case 'invalid':
        return { color: 'text-white/40', icon: null, text: 'Ingresá al menos 3 caracteres.' };
      default:
        return null;
    }
  })();

  return (
    <div
      ref={containerRef}
      className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/[0.05] to-transparent p-4 sm:p-5"
      data-testid="bravx-referral-widget"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0">
          <Heart size={14} className="text-[#D4AF37]" />
        </div>
        <div className="flex-1 min-w-0">
          <h4
            className="text-sm font-extrabold text-white uppercase tracking-wide flex items-center gap-1.5"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            Apoyá a un Bravx
            <Sparkles size={12} className="text-[#D4AF37]" />
          </h4>
          <p className="text-[11px] text-white/50 mt-0.5 leading-relaxed">
            Empezá a escribir y elegí al usuario que te recomendó. Suma puntos para los dos.
          </p>
        </div>
      </div>

      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm pointer-events-none">@</span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            const v = e.target.value.replace(/^@/, '').replace(/[^a-zA-Z0-9._]/g, '').toLowerCase().slice(0, 24);
            onChange(v);
          }}
          onFocus={() => {
            setIsFocused(true);
            if (suggestions.length > 0) setShowDropdown(true);
          }}
          onBlur={() => {
            // Delay to allow click on suggestion
            setTimeout(() => setIsFocused(false), 150);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Buscar por nombre o @usuario…"
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          className={`w-full pl-7 pr-3 py-2.5 rounded-lg bg-black/40 border text-sm text-white placeholder-white/30 focus:outline-none transition-colors ${
            state === 'found' ? 'border-emerald-500/60' :
            state === 'self' ? 'border-amber-500/60' :
            state === 'not_found' ? 'border-red-500/60' :
            'border-white/10 focus:border-white/30'
          }`}
          data-testid="bravx-referral-input"
        />

        {/* Typeahead dropdown — rendered via portal with fixed positioning so it
            escapes overflow:hidden parents (the checkout form card clips children). */}
        {showDropdown && suggestions.length > 0 && createPortal(
          <div
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
            }}
            className="z-[100] rounded-xl border border-white/15 bg-zinc-950/95 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden max-h-72 overflow-y-auto"
            data-testid="bravx-referral-dropdown"
          >
            <div className="px-3 py-2 border-b border-white/5 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40">
              <Search size={10} />
              <span>Resultados ({suggestions.length})</span>
            </div>
            {suggestions.map((sug, i) => (
              <button
                key={sug.username}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickSuggestion(sug)}
                onMouseEnter={() => setActiveIdx(i)}
                data-testid={`bravx-referral-suggestion-${sug.username}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-b border-white/[0.03] last:border-b-0 ${
                  activeIdx === i ? 'bg-[#D4AF37]/10' : 'hover:bg-white/[0.04]'
                }`}
              >
                {sug.avatar_url ? (
                  <img src={sug.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-1 ring-white/10" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37]/30 to-[#D4AF37]/5 flex items-center justify-center flex-shrink-0 ring-1 ring-[#D4AF37]/20">
                    <span className="text-[#D4AF37] text-xs font-bold">{(sug.display_name || sug.username).slice(0, 2).toUpperCase()}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate font-medium">{sug.display_name}</p>
                  <p className="text-[11px] text-white/50 truncate">@{sug.username}</p>
                </div>
                {sug.bravx_points > 0 && (
                  <span className="text-[10px] font-mono font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full px-2 py-0.5 flex-shrink-0">
                    {sug.bravx_points} pts
                  </span>
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
      </div>

      {helper && !showDropdown && (
        <div
          className={`mt-2 flex items-center gap-1.5 text-[11px] ${helper.color}`}
          data-testid="bravx-referral-helper"
        >
          {helper.icon}
          <span>{helper.text}</span>
        </div>
      )}
    </div>
  );
}
